import React, { useState, useCallback } from 'react';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { AlertTriangle, Trash2, ShieldOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Panic Button Component
 * Instantly wipes all local data including encryption keys
 * Use in emergencies when device security is compromised
 */

export const PanicButton = ({ onActivate, renderTrigger }) => {
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [isWiping, setIsWiping] = useState(false);
  const [step, setStep] = useState(1);

  const CONFIRM_PHRASE = 'WIPE ALL DATA';

  const handlePanic = useCallback(async () => {
    if (confirmText !== CONFIRM_PHRASE) {
      toast.error('Please type the confirmation phrase exactly');
      return;
    }

    setIsWiping(true);
    
    try {
      // Step 1: Clear encryption keys
      localStorage.removeItem('ghostcomm_keys');
      
      // Step 2: Clear auth token
      localStorage.removeItem('ghostcomm_token');
      
      // Step 3: Clear all localStorage
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith('ghostcomm') || key?.includes('ghost') || key?.includes('comm')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
      
      // Step 4: Clear sessionStorage
      sessionStorage.clear();
      
      // Step 5: Clear IndexedDB (if used)
      if (window.indexedDB) {
        const databases = await window.indexedDB.databases?.() || [];
        databases.forEach(db => {
          if (db.name) {
            window.indexedDB.deleteDatabase(db.name);
          }
        });
      }
      
      // Step 6: Clear cookies
      document.cookie.split(';').forEach(cookie => {
        const name = cookie.split('=')[0].trim();
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
      });
      
      // Step 7: Clear cache (if available)
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
      }

      // Notify parent
      if (onActivate) {
        onActivate();
      }

      // Show success and redirect
      toast.success('All data wiped successfully');
      
      // Force reload to login page
      setTimeout(() => {
        window.location.href = '/login';
      }, 1000);
      
    } catch (error) {
      console.error('Wipe error:', error);
      // Even if there's an error, try to redirect
      toast.error('Partial wipe completed');
      setTimeout(() => {
        window.location.href = '/login';
      }, 1000);
    }
  }, [confirmText, onActivate]);

  const handleClose = () => {
    setShowConfirm(false);
    setConfirmText('');
    setStep(1);
    setIsWiping(false);
  };

  return (
    <>
      {renderTrigger ? (
        renderTrigger(() => setShowConfirm(true))
      ) : (
        <Button
          variant="destructive"
          onClick={() => setShowConfirm(true)}
          className="w-full bg-red-600 hover:bg-red-700 text-white"
          data-testid="panic-button"
        >
          <ShieldOff className="w-4 h-4 mr-2" />
          Emergency Data Wipe
        </Button>
      )}

      <AlertDialog open={showConfirm} onOpenChange={handleClose}>
        <AlertDialogContent className="bg-card border-border max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-400">
              <AlertTriangle className="w-5 h-5" />
              Emergency Data Wipe
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-4">
              {step === 1 && (
                <>
                  <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300">
                    <p className="font-medium mb-2">This action will immediately:</p>
                    <ul className="text-sm space-y-1 list-disc list-inside">
                      <li>Delete all encryption keys (messages become unreadable)</li>
                      <li>Clear your authentication session</li>
                      <li>Remove all local data and cache</li>
                      <li>Log you out of Government Shield</li>
                    </ul>
                  </div>
                  <p className="text-slate-400 text-sm">
                    This cannot be undone. Your encryption keys will be permanently destroyed.
                  </p>
                </>
              )}

              {step === 2 && (
                <div className="space-y-4">
                  <p className="text-slate-300">
                    To confirm, type <span className="font-mono text-red-400 bg-red-500/10 px-2 py-1 rounded">{CONFIRM_PHRASE}</span> below:
                  </p>
                  <Input
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder="Type confirmation phrase"
                    className="font-mono bg-secondary/50 border-red-500/30 focus:border-red-500"
                    autoComplete="off"
                    data-testid="panic-confirm-input"
                  />
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              onClick={handleClose}
              disabled={isWiping}
            >
              Cancel
            </AlertDialogCancel>
            {step === 1 ? (
              <Button
                variant="destructive"
                onClick={() => setStep(2)}
                className="bg-red-600 hover:bg-red-700"
              >
                Continue
              </Button>
            ) : (
              <Button
                variant="destructive"
                onClick={handlePanic}
                disabled={confirmText !== CONFIRM_PHRASE || isWiping}
                className="bg-red-600 hover:bg-red-700"
                data-testid="panic-confirm-btn"
              >
                {isWiping ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Wiping...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Wipe All Data
                  </>
                )}
              </Button>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

// Compact panic trigger for quick access
export const PanicTrigger = ({ onClick }) => {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 transition-colors text-xs font-medium"
      title="Emergency data wipe"
      data-testid="panic-trigger"
    >
      <ShieldOff className="w-3 h-3" />
      <span>Panic</span>
    </button>
  );
};
